import { Cloud, Monitor, Users, Circle, ChevronDown } from 'lucide-react';
import { useRegistryContext } from '@/contexts/RegistryContext';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const kindIcons: Record<string, typeof Cloud> = {
    local: Monitor,
    cloud: Cloud,
    partner: Users,
};

const statusColors: Record<string, string> = {
    active: 'text-emerald-500',
    disabled: 'text-slate-400',
    unreachable: 'text-red-500',
};

export function RegistrySelector() {
    const { registries, activeRegistryId, activeRegistry, setActiveRegistryId } =
        useRegistryContext();

    if (registries.length <= 1) return null;

    const Icon = kindIcons[activeRegistry?.kind ?? 'cloud'] ?? Cloud;
    const statusColor = statusColors[activeRegistry?.status ?? 'active'] ?? 'text-slate-400';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-md border border-sidebar-border bg-sidebar px-2.5 py-1.5 text-xs font-medium text-sidebar-foreground hover:bg-sidebar-accent w-full group-data-[collapsible=icon]:hidden">
                    <Circle size={8} className={`fill-current ${statusColor}`} />
                    <Icon size={14} />
                    <span className="truncate flex-1 text-left">
                        {activeRegistry?.display_name ?? activeRegistry?.alias ?? 'Select'}
                    </span>
                    <ChevronDown size={12} className="text-sidebar-foreground/50" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
                {registries.map((reg) => {
                    const RegIcon = kindIcons[reg.kind] ?? Cloud;
                    const sc = statusColors[reg.status] ?? 'text-slate-400';
                    const isActive = reg.id === activeRegistryId;

                    return (
                        <DropdownMenuItem
                            key={reg.id}
                            onClick={() => setActiveRegistryId(reg.id)}
                            className={isActive ? 'bg-accent' : ''}
                        >
                            <Circle size={8} className={`fill-current ${sc}`} />
                            <RegIcon size={14} />
                            <span className="flex-1 truncate">
                                {reg.display_name ?? reg.alias}
                            </span>
                            {reg.is_default && (
                                <span className="text-[10px] text-muted-foreground">default</span>
                            )}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
